import { useEffect, useMemo, useRef, useState } from "react";
import LoadingOverlay from "@/components/LoadingOverlay";
import { PdfGenerator } from "@/components/PdfGenerator";
import PdfViewDrawer from "@/components/PdfViewDrawer";
import { useLoadingContext } from "@/context/LoadingProvider";
import useDisclosure, { DisclosureHost } from "@/hooks/use-disclosure";
import { useEncounterSummeryV2 } from "@/queries/encounter/use-encounter-summery-v2";
import EncounterSummeryTemplate, {
  useResolveEncounterLogo,
} from "@/template/EncounterTemplate";
import { getXtenantId } from "@/utils/utils";

/**
 * ViewEncounterDrawerProps interface to define the props for the ViewEncounterDrawer component
 *
 * @interface ViewEncounterDrawerProps
 * @property {any} appointmentId - Appointment ID
 */
interface ViewEncounterDrawerProps {
  disclosure: DisclosureHost;
  onClose?: () => void;
  /** Leaves Chief Complaint out of the report, for notes that never capture one. */
  hideChiefComplaint?: boolean;
}

/**
 * ViewEncounterDrawer component to view the encounter summary
 *
 * @see disclosure {Must pass appointmentId in this disclosure EX- ` disclosure.open(appointmentId)`}
 *
 * @returns ViewEncounterDrawer component
 *
 * @author [Rajkumar Rathod]
 */
const ViewEncounterSummaryDrawer = ({
  onClose,
  disclosure,
  hideChiefComplaint = false,
}: ViewEncounterDrawerProps) => {
  const pdfRef = useRef<{ generatePdf: (name?: string) => void }>(null);
  const { setIsLoading } = useLoadingContext();
  const {
    data: encounterPrefillData,
    isLoading,
    isPending,
    isFetching,
  } = useEncounterSummeryV2({
    appointmentUuid: disclosure?.data ?? "",
    xTENANTID: getXtenantId(),
    enabled: !!disclosure?.data && disclosure.opened,
  });
  const resolvedData = useResolveEncounterLogo(encounterPrefillData?.data);
  const reportData = useMemo(
    () =>
      resolvedData && hideChiefComplaint
        ? { ...resolvedData, chiefComplaint: undefined }
        : resolvedData,
    [resolvedData, hideChiefComplaint]
  );
  const [previewFile, setPreviewFile] = useState<string>("");
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);
  const isResolvingLogo = !!encounterPrefillData?.data && !resolvedData;
  const printViewDisclosure = useDisclosure(false, {
    onClose: disclosure.close,
  });

  useEffect(() => {
    if (resolvedData && !isLoading && !isFetching) {
      setIsGeneratingPdf(true);
      pdfRef?.current?.generatePdf();
    }
  }, [resolvedData, isLoading, isFetching]);

  if (!disclosure?.data || !disclosure.opened) return null;

  return (
    <>
      <LoadingOverlay
        isLoading={
          isFetching ||
          isLoading ||
          isPending ||
          isResolvingLogo ||
          isGeneratingPdf
        }
      />

      {/* Hidden PDF component with prefilled data */}
      <PdfGenerator
        ref={pdfRef}
        onSave={(blob) => {
          setIsLoading(false);
          setIsGeneratingPdf(false);
          if (!blob) return;
          const url = window.URL.createObjectURL(blob);
          setPreviewFile(url);
          printViewDisclosure.open();
        }}
      >
        <EncounterSummeryTemplate data={reportData} />
      </PdfGenerator>

      <PdfViewDrawer
        w="65vw"
        file={previewFile}
        disclosureHost={printViewDisclosure}
        onClose={() => {
          onClose ? onClose() : disclosure.close();
        }}
        title="Visit"
      />
    </>
  );
};

export default ViewEncounterSummaryDrawer;
